import React, { ReactElement, ReactNode } from "react";
import { FormFieldReplacementInput, FormFieldType } from "../../../__generated__/api";
import { ReplacementGroup } from "../../replacement-group";
import Select, { Option, OptionWithChildren } from "../../select/Select";
import { Filters } from "../../filters/Filters";

interface Props<TRequired extends boolean, TReplacement extends ReplacementGroup[] | undefined> {
    isRequired?: TRequired;
    value?: TRequired extends true ? boolean : (boolean | undefined);
    placeholder?: string;
    className?: string;
    input?: ReplacementGroup[] | undefined;

    shouldShowDropdown?: boolean;
    toggleClassName?: string;
    toggleText?: string
    icon?: ReactNode;

    onChange: (
        value: TRequired extends true
            ? (([TReplacement] extends ReplacementGroup ? string : boolean) | boolean)
            : (([TReplacement] extends ReplacementGroup ? string : boolean) | boolean | undefined),
        text?: TRequired extends true ? string : (string | undefined),
        replacements?: FormFieldReplacementInput[]
    ) => void;
}

const VALUES = [
    {
        label: "Yes",
        value: "true",
        data: true,
    },
    {
        label: "No",
        value: "false",
        data: false,
    },
];

interface FormWithLabel extends FormFieldReplacementInput {
    groupLabel: string
}

export default function BooleanSelect<TRequired extends boolean, TReplacement extends ReplacementGroup[] | undefined>({
    onChange: onSelected, isRequired, value, placeholder, className, input,
    shouldShowDropdown, toggleClassName, toggleText, icon,
}: Props<TRequired, TReplacement>): ReactElement {
    let values: Option<boolean | FormWithLabel>[] |
    OptionWithChildren<boolean | FormWithLabel>[] = VALUES;
    let flattendValues = values;
    const applicableReplacements = input?.map(row => ({
        ...row,
        input: row.input?.filter(rowInput => rowInput.type === FormFieldType.Boolean),
    })).filter(row => row.input?.isNotEmpty);

    if (applicableReplacements?.isNotEmpty) {
        values = applicableReplacements.map(group => ({
            label: group.label,
            options: group.input?.map(inputType => ({
                label: `${group.label}/${inputType.name}`,
                value: `${group.id}/${inputType.id}`,
                data: {
                    fieldId: inputType.id,
                    groupId: group.id,
                    replacement: `${group.id}/${inputType.id}`,
                    groupLabel: `${group.label}/${inputType.name}`,
                } as FormWithLabel | boolean,
            })),
        })).concat([
            {
                label: "Manual",
                options: VALUES,
            },
        ]) as OptionWithChildren<boolean | FormWithLabel>[];
        flattendValues = values.flatMap(row => row.options);
    }

    const handleSelected = (selected: Option<boolean | FormWithLabel>) => {
        if (!selected) {
            (onSelected as (empty: unknown) => void)(undefined);
            return;
        }
        const selectedValue = selected.data;

        if (typeof selectedValue === "boolean") {
            onSelected(selectedValue as boolean, selectedValue ? "Yes" : "No");
        } else {
            type LabelType = TRequired extends true ? boolean : boolean | undefined
            onSelected(selectedValue?.replacement as LabelType, selectedValue?.groupLabel, selectedValue ? [{
                fieldId: selectedValue.fieldId,
                groupId: selectedValue.groupId,
                replacement: selectedValue.replacement,
            }] : []);
        }
    };

    const selectedValue = flattendValues.find(option => option.value === value?.toString());

    if (shouldShowDropdown) {
        return (
            <Filters
                isMulti={false}
                toggleText={toggleText}
                icon={icon}
                categories={values}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={selectedValue?.value}
                onSelected={current => onSelected(current === "true")}
            />
        );
    }
    return (
        <Select
            className={className}
            minRequired={isRequired ? 1 : 0}
            isClearable={!isRequired}
            placeholder={placeholder}
            onChange={option => handleSelected(option)}
            options={values}
            value={selectedValue}
        />
    );
}
BooleanSelect.defaultProps = {
    isRequired: false,
    value: undefined,
    placeholder: undefined,
    className: "",
    input: [],

    shouldShowDropdown: undefined,
    toggleClassName: undefined,
    toggleText: undefined,
    icon: undefined,
};
